import { useState, useEffect, memo } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { FileText, TrendingUp, TrendingDown, Download, BarChart3, PieChart, Calendar, DollarSign, ShoppingCart, Receipt, Mail, Save, Send, Loader2, Users, Clock } from 'lucide-react';
import { useSettings } from '@/contexts/SettingsContext';
import StandardPageLayout from '@/components/StandardPageLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Rapports = ({ user }) => {
  const { settings } = useSettings();
  const currencySymbol = settings.currency_symbol || 'DT';
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [categories, setCategories] = useState([]);
  
  // Date range - default to current year
  const currentYear = new Date().getFullYear();
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  // Report email config state
  const isAdmin = user?.role === 'admin';
  const [emailConfig, setEmailConfig] = useState({
    enabled: false,
    frequency: 'weekly',
    time_of_day: '08:00',
    custom_day: 1,
    period: 'this_month',
    recipient_user_ids: [],
    template_id: null,
  });
  const [emailStatus, setEmailStatus] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  // Quick period buttons
  const setQuickPeriod = (period) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    
    switch (period) {
      case 'this_month':
        setStartDate(`${year}-${String(month + 1).padStart(2, '0')}-01`);
        setEndDate(today.toISOString().split('T')[0]);
        break;
      case 'last_month':
        const lastMonth = month === 0 ? 11 : month - 1;
        const lastMonthYear = month === 0 ? year - 1 : year;
        const lastDayOfLastMonth = new Date(lastMonthYear, lastMonth + 1, 0).getDate();
        setStartDate(`${lastMonthYear}-${String(lastMonth + 1).padStart(2, '0')}-01`);
        setEndDate(`${lastMonthYear}-${String(lastMonth + 1).padStart(2, '0')}-${lastDayOfLastMonth}`);
        break;
      case 'this_quarter':
        const quarterStart = Math.floor(month / 3) * 3;
        setStartDate(`${year}-${String(quarterStart + 1).padStart(2, '0')}-01`);
        setEndDate(today.toISOString().split('T')[0]);
        break;
      case 'this_year':
        setStartDate(`${year}-01-01`);
        setEndDate(today.toISOString().split('T')[0]);
        break;
      case 'last_year':
        setStartDate(`${year - 1}-01-01`);
        setEndDate(`${year - 1}-12-31`);
        break;
      case 'all_time':
        setStartDate('2020-01-01');
        setEndDate(today.toISOString().split('T')[0]);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    fetchCategories();
    if (isAdmin) {
      fetchOrgUsers();
      fetchEmailConfig();
      fetchEmailTemplates();
    }
  }, [isAdmin]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/expense-categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.nom : catId;
  };

  // ===== Email Config Functions =====
  const fetchOrgUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setOrgUsers(response.data.filter(u => u.is_active !== false));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await axios.get(`${API}/email-templates/`);
      setEmailTemplates(response.data.filter(t => t.type === 'report'));
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  };

  const fetchEmailConfig = async () => {
    try {
      const response = await axios.get(`${API}/reports/email-config`);
      setEmailConfig({
        enabled: response.data.enabled || false,
        frequency: response.data.frequency || 'weekly',
        time_of_day: response.data.time_of_day || '08:00',
        custom_day: response.data.custom_day || 1,
        period: response.data.period || 'this_month',
        recipient_user_ids: response.data.recipient_user_ids || [],
        template_id: response.data.template_id || null,
      });
      fetchEmailStatus();
    } catch (error) {
      console.error('Error fetching email config:', error);
    }
  };

  const fetchEmailStatus = async () => {
    try {
      const response = await axios.get(`${API}/reports/email-config/status`);
      setEmailStatus(response.data);
    } catch (error) {
      console.error('Error fetching email status:', error);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      await axios.post(`${API}/reports/email-config`, emailConfig);
      toast.success('Configuration du récap email enregistrée');
      fetchEmailStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSavingEmail(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const response = await axios.post(`${API}/reports/email-config/test`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du test");
    } finally {
      setTestingEmail(false);
    }
  };

  const handleResetLastSent = async () => {
    try {
      const response = await axios.post(`${API}/reports/email-config/reset-last-sent`);
      toast.success(response.data.message);
      fetchEmailStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const toggleRecipient = (userId) => {
    const newIds = emailConfig.recipient_user_ids.includes(userId)
      ? emailConfig.recipient_user_ids.filter(id => id !== userId)
      : [...emailConfig.recipient_user_ids, userId];
    setEmailConfig({ ...emailConfig, recipient_user_ids: newIds });
  };

  const fetchReport = async () => {
    if (!startDate || !endDate) {
      toast.error('Veuillez sélectionner les dates de début et de fin');
      return;
    }
    
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('La date de début doit être antérieure à la date de fin');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/financial?start_date=${startDate}&end_date=${endDate}`);
      setReport(response.data);
      toast.success('Rapport généré avec succès');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Erreur lors de la génération du rapport');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit'
    });
  };

  const exportToPDF = () => {
    if (!report) return;

    const monthlyRows = Object.entries(report.monthly_data || {}).map(([month, data]) => {
      return `
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #ddd;">${data.month_name || month}</td>
          <td style="padding: 8px; border-bottom: 1px solid #ddd; color: green;">${data.ventes.toFixed(2)} ${currencySymbol}</td>
          <td style="padding: 8px; border-bottom: 1px solid #ddd; color: orange;">${data.achats.toFixed(2)} ${currencySymbol}</td>
          <td style="padding: 8px; border-bottom: 1px solid #ddd; color: red;">${data.depenses.toFixed(2)} ${currencySymbol}</td>
          <td style="padding: 8px; border-bottom: 1px solid #ddd; font-weight: bold; color: ${(data.ventes - data.achats - data.depenses) >= 0 ? 'green' : 'red'};">
            ${(data.ventes - data.achats - data.depenses).toFixed(2)} ${currencySymbol}
          </td>
        </tr>
      `;
    }).join('');

    const categoryRows = Object.entries(report.depenses_par_categorie || {}).map(([catId, amount]) => `
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #ddd;">${getCategoryName(catId)}</td>
        <td style="padding: 8px; border-bottom: 1px solid #ddd;">${amount.toFixed(2)} ${currencySymbol}</td>
      </tr>
    `).join('');

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Rapport Financier - Firma</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
          h1 { color: #10B981; border-bottom: 2px solid #10B981; padding-bottom: 10px; font-size: 18px; }
          h2 { color: #333; margin-top: 20px; font-size: 14px; }
          .period { background: #f0fdf4; padding: 8px; border-radius: 8px; margin-bottom: 15px; }
          .summary { display: flex; justify-content: space-between; margin: 15px 0; flex-wrap: wrap; }
          .summary-card { padding: 10px; border-radius: 8px; text-align: center; width: 22%; margin-bottom: 10px; }
          .ventes { background: #D1FAE5; }
          .achats { background: #FED7AA; }
          .depenses { background: #FECACA; }
          .benefice { background: ${report.benefice_net >= 0 ? '#D1FAE5' : '#FECACA'}; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th { background: #10B981; color: white; padding: 8px; text-align: left; font-size: 11px; }
          td { font-size: 11px; }
          .footer { margin-top: 30px; text-align: center; color: #666; font-size: 10px; }
        </style>
      </head>
      <body>
        <h1>🐑 Rapport Financier</h1>
        <div class="period">
          <strong>Période:</strong> Du ${formatDate(report.start_date)} au ${formatDate(report.end_date)}
        </div>
        
        <div class="summary">
          <div class="summary-card ventes">
            <h3 style="margin:0;font-size:12px;">Ventes</h3>
            <p style="font-size: 16px; font-weight: bold; color: #059669; margin:5px 0;">${report.total_ventes.toFixed(2)} ${currencySymbol}</p>
          </div>
          <div class="summary-card achats">
            <h3 style="margin:0;font-size:12px;">Achats</h3>
            <p style="font-size: 16px; font-weight: bold; color: #EA580C; margin:5px 0;">${report.total_achats.toFixed(2)} ${currencySymbol}</p>
          </div>
          <div class="summary-card depenses">
            <h3 style="margin:0;font-size:12px;">Dépenses</h3>
            <p style="font-size: 16px; font-weight: bold; color: #DC2626; margin:5px 0;">${report.total_depenses.toFixed(2)} ${currencySymbol}</p>
          </div>
          <div class="summary-card benefice">
            <h3 style="margin:0;font-size:12px;">Bénéfice Net</h3>
            <p style="font-size: 16px; font-weight: bold; color: ${report.benefice_net >= 0 ? '#059669' : '#DC2626'}; margin:5px 0;">${report.benefice_net.toFixed(2)} ${currencySymbol}</p>
          </div>
        </div>

        <h2>📁 Dépenses par catégorie</h2>
        <table>
          <thead><tr><th>Catégorie</th><th>Montant</th></tr></thead>
          <tbody>${categoryRows}</tbody>
        </table>

        <h2>📊 Détails mensuels</h2>
        <table>
          <thead><tr><th>Mois</th><th>Ventes</th><th>Achats</th><th>Dépenses</th><th>Solde</th></tr></thead>
          <tbody>${monthlyRows}</tbody>
        </table>

        <div class="footer">
          <p>Rapport généré le ${new Date().toLocaleDateString('fr-FR')} par Firma</p>
        </div>
      </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rapport_${startDate}_${endDate}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Rapport exporté');
  };

  // Calculate max value for chart scaling
  const getMaxValue = () => {
    if (!report?.monthly_data) return 1000;
    let max = 0;
    Object.values(report.monthly_data).forEach(data => {
      max = Math.max(max, data.ventes, data.achats, data.depenses);
    });
    return max || 1000;
  };

  // Limit monthly data for chart display (max 12 months)
  const getChartData = () => {
    if (!report?.monthly_data) return {};
    const entries = Object.entries(report.monthly_data);
    if (entries.length <= 12) return report.monthly_data;
    // Take last 12 months
    return Object.fromEntries(entries.slice(-12));
  };

  return (
    <StandardPageLayout
      title="Rapports Financiers"
      subtitle="Analysez les performances de votre ferme"
      icon={<BarChart3 className="w-6 h-6 sm:w-8 sm:h-8 text-slate-700 dark:text-slate-300" />}
    >
      <div className="w-full overflow-hidden">
        {/* Configuration Card */}
        <Card className="border-0 shadow-lg mb-4 sm:mb-6">
          <CardHeader className="bg-slate-800 text-white dark:bg-slate-700 rounded-t-lg py-3 sm:py-4">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
              Sélection de la période
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 space-y-3 sm:space-y-4">
            {/* Quick Period Buttons - Scrollable on mobile */}
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {[
                { key: 'this_month', label: 'Ce mois' },
                { key: 'last_month', label: 'Mois dernier' },
                { key: 'this_quarter', label: 'Trimestre' },
                { key: 'this_year', label: 'Cette année' },
                { key: 'last_year', label: 'Année dernière' },
                { key: 'all_time', label: 'Tout' },
              ].map(({ key, label }) => (
                <Button
                  key={key}
                  variant="outline"
                  size="sm"
                  onClick={() => setQuickPeriod(key)}
                  className="text-xs whitespace-nowrap flex-shrink-0"
                >
                  {label}
                </Button>
              ))}
            </div>

            {/* Date Range Inputs - Stack on mobile */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:items-end">
              <div className="flex gap-2 flex-1">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="start_date" className="text-xs sm:text-sm">Début</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="text-sm h-9 w-full"
                    data-testid="report-start-date"
                  />
                </div>

                <div className="flex-1 space-y-1">
                  <Label htmlFor="end_date" className="text-xs sm:text-sm">Fin</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="text-sm h-9 w-full"
                    data-testid="report-end-date"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={fetchReport}
                  disabled={loading}
                  className="flex-1 sm:flex-none bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 h-9 text-sm"
                  data-testid="generate-report-button"
                >
                  <FileText className="w-4 h-4 mr-1" />
                  {loading ? '...' : 'Générer'}
                </Button>

                {report && (
                  <Button
                    onClick={exportToPDF}
                    variant="outline"
                    className="flex-1 sm:flex-none border-slate-500 text-slate-700 dark:text-slate-300 h-9 text-sm"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Exporter
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

      {/* Report Results */}
      {report && (
        <div className="space-y-4 sm:space-y-6 animate-fade-in">
          {/* Period Info */}
          <div className="bg-slate-50 dark:bg-slate-900/30 border border-gray-200 dark:border-gray-800 rounded-lg p-2 sm:p-3 text-center text-xs sm:text-sm">
            <p className="text-slate-800 dark:text-slate-200">
              📅 <strong>{formatDate(report.start_date)}</strong> → <strong>{formatDate(report.end_date)}</strong>
            </p>
          </div>

          {/* Summary Cards - 2 columns on mobile */}
          <div className="grid grid-cols-2 gap-2 sm:gap-4">
            <Card className="border-0 shadow-md bg-slate-50 dark:bg-slate-900/30">
              <CardContent className="p-3">
                <p className="text-[10px] sm:text-xs font-medium text-slate-800 dark:text-slate-200">Ventes</p>
                <p className="text-base sm:text-xl font-bold text-slate-700 dark:text-slate-300">{report.total_ventes.toFixed(0)}</p>
                <p className="text-[10px] sm:text-xs text-slate-600 dark:text-slate-400">{currencySymbol}</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950 dark:to-orange-900">
              <CardContent className="p-3">
                <p className="text-[10px] sm:text-xs font-medium text-orange-700 dark:text-orange-300">Achats</p>
                <p className="text-base sm:text-xl font-bold text-orange-600">{report.total_achats.toFixed(0)}</p>
                <p className="text-[10px] sm:text-xs text-orange-500">{currencySymbol}</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950 dark:to-red-900">
              <CardContent className="p-3">
                <p className="text-[10px] sm:text-xs font-medium text-red-700 dark:text-red-300">Dépenses</p>
                <p className="text-base sm:text-xl font-bold text-red-600">{report.total_depenses.toFixed(0)}</p>
                <p className="text-[10px] sm:text-xs text-red-500">{currencySymbol}</p>
              </CardContent>
            </Card>

            <Card className={`border-0 shadow-md ${
              report.benefice_net >= 0 
                ? 'bg-slate-100 dark:bg-slate-800/50' 
                : 'bg-gradient-to-br from-red-100 to-rose-100 dark:from-red-900 dark:to-rose-900'
            }`}>
              <CardContent className="p-3">
                <p className={`text-[10px] sm:text-xs font-medium ${report.benefice_net >= 0 ? 'text-slate-800 dark:text-slate-200' : 'text-red-700'}`}>
                  Bénéfice
                </p>
                <p className={`text-base sm:text-xl font-bold ${report.benefice_net >= 0 ? 'text-slate-700 dark:text-slate-300' : 'text-red-600'}`}>
                  {report.benefice_net.toFixed(0)}
                </p>
                <p className={`text-[10px] sm:text-xs ${report.benefice_net >= 0 ? 'text-slate-600 dark:text-slate-400' : 'text-red-500'}`}>{currencySymbol}</p>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Chart - Fixed height, scrollable */}
          {Object.keys(report.monthly_data || {}).length > 0 && (
            <Card className="border-0 shadow-lg">
              <CardHeader className="py-3 sm:py-4">
                <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                  <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-slate-700 dark:text-slate-300" />
                  Évolution mensuelle
                  {Object.keys(report.monthly_data).length > 12 && (
                    <span className="text-xs text-gray-500 font-normal">(12 derniers mois)</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4 overflow-hidden">
                {/* Chart bars - Fixed height */}
                <div className="flex items-end justify-around gap-1 h-32 sm:h-40 mb-2">
                  {Object.entries(getChartData()).map(([month, data]) => {
                    const maxVal = getMaxValue();
                    const ventesHeight = (data.ventes / maxVal) * 100;
                    const achatsHeight = (data.achats / maxVal) * 100;
                    const depensesHeight = (data.depenses / maxVal) * 100;
                    
                    return (
                      <div key={month} className="flex-1 flex flex-col items-center max-w-[35px]">
                        <div className="flex gap-[1px] items-end h-24 sm:h-32">
                          <div 
                            className="w-[6px] sm:w-2 bg-slate-50 dark:bg-slate-900/300 rounded-t" 
                            style={{ height: `${Math.max(ventesHeight, 2)}px` }}
                          />
                          <div 
                            className="w-[6px] sm:w-2 bg-orange-500 rounded-t" 
                            style={{ height: `${Math.max(achatsHeight, 2)}px` }}
                          />
                          <div 
                            className="w-[6px] sm:w-2 bg-red-500 rounded-t" 
                            style={{ height: `${Math.max(depensesHeight, 2)}px` }}
                          />
                        </div>
                        <span className="text-[8px] sm:text-[10px] text-gray-500 mt-1 truncate w-full text-center">
                          {(data.month_name || month).slice(0, 3)}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {/* Legend */}
                <div className="flex justify-center gap-2 sm:gap-4 mt-2 text-[10px] sm:text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 bg-slate-50 dark:bg-slate-900/300 rounded" />
                    <span className="text-gray-600 dark:text-gray-400">Ventes</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 bg-orange-500 rounded" />
                    <span className="text-gray-600 dark:text-gray-400">Achats</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 bg-red-500 rounded" />
                    <span className="text-gray-600 dark:text-gray-400">Dépenses</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Categories + Transactions side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Expenses by Category */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="py-3 sm:py-4">
                <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                  <PieChart className="w-4 h-4 sm:w-5 sm:h-5 text-slate-700 dark:text-slate-300" />
                  Dépenses par catégorie
                </CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(report.depenses_par_categorie || {}).length === 0 ? (
                  <p className="text-gray-500 text-center py-6 text-sm">Aucune dépense</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {Object.entries(report.depenses_par_categorie || {})
                      .sort((a, b) => b[1] - a[1])
                      .map(([catId, amount], index) => {
                        const total = report.total_depenses || 1;
                        const percentage = ((amount / total) * 100).toFixed(0);
                        const colors = ['bg-slate-50 dark:bg-slate-900/300', 'bg-blue-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500'];
                        const color = colors[index % colors.length];
                        
                        return (
                          <div key={catId} className="space-y-1">
                            <div className="flex justify-between text-xs sm:text-sm">
                              <span className="font-medium truncate mr-2">{getCategoryName(catId)}</span>
                              <span className="text-gray-600 whitespace-nowrap">{amount.toFixed(0)} {currencySymbol}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className={`${color} h-2 rounded-full`} style={{ width: `${percentage}%` }} />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Transactions Details */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="py-3 sm:py-4">
                <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                  <Receipt className="w-4 h-4 sm:w-5 sm:h-5 text-slate-700 dark:text-slate-300" />
                  Détails des transactions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(!report.transactions || report.transactions.length === 0) ? (
                  <p className="text-gray-500 text-center py-6 text-sm">Aucune transaction</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {report.transactions.slice(0, 20).map((tx, index) => (
                      <div key={tx.id || index} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`text-lg ${tx.type === 'vente' ? 'text-slate-600 dark:text-slate-400' : 'text-orange-500'}`}>
                            {tx.type === 'vente' ? '↑' : '↓'}
                          </span>
                          <div className="min-w-0">
                            <p className="text-xs sm:text-sm font-medium truncate">
                              {tx.type === 'vente' ? 'Vente' : 'Achat'}
                            </p>
                            <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">{formatDateShort(tx.date)}</p>
                          </div>
                        </div>
                        <span className={`text-sm font-bold whitespace-nowrap ${tx.type === 'vente' ? 'text-slate-700 dark:text-slate-300' : 'text-orange-600'}`}>
                          {tx.type === 'vente' ? '+' : '-'}{(tx.prix || tx.montant || 0).toFixed(0)} {currencySymbol}
                        </span>
                      </div>
                    ))}
                    {report.transactions.length > 20 && (
                      <p className="text-xs text-gray-400 text-center pt-2">
                        +{report.transactions.length - 20} autres transactions
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Monthly Details Table - At bottom */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <CardHeader className="py-3 sm:py-4">
              <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-slate-700 dark:text-slate-300" />
                Détails mensuels
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 sm:p-6">
              <div className="overflow-x-auto">
                <table className="w-full text-[10px] sm:text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-2 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Mois</th>
                      <th className="px-1 sm:px-2 py-2 text-right font-medium text-slate-700 dark:text-slate-300">Ventes</th>
                      <th className="px-1 sm:px-2 py-2 text-right font-medium text-orange-600">Achats</th>
                      <th className="px-1 sm:px-2 py-2 text-right font-medium text-red-600">Dép.</th>
                      <th className="px-1 sm:px-2 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Solde</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(report.monthly_data || {}).map(([month, data]) => {
                      const solde = data.ventes - data.achats - data.depenses;
                      return (
                        <tr key={month} className="border-b dark:border-gray-700">
                          <td className="px-2 py-2 font-medium whitespace-nowrap">{(data.month_name || month).slice(0, 8)}</td>
                          <td className="px-1 sm:px-2 py-2 text-right text-slate-700 dark:text-slate-300">{data.ventes.toFixed(0)}</td>
                          <td className="px-1 sm:px-2 py-2 text-right text-orange-600">{data.achats.toFixed(0)}</td>
                          <td className="px-1 sm:px-2 py-2 text-right text-red-600">{data.depenses.toFixed(0)}</td>
                          <td className={`px-1 sm:px-2 py-2 text-right font-medium ${solde >= 0 ? 'text-slate-700 dark:text-slate-300' : 'text-red-600'}`}>
                            {solde.toFixed(0)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Stats Footer */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              📊 <strong>{report.nb_transactions || 0}</strong> transactions • 
              💰 <strong>{report.nb_depenses || 0}</strong> dépenses
            </p>
          </div>
        </div>
      )}

        {/* Empty State */}
        {!report && !loading && (
          <Card className="border-0 shadow-lg">
            <CardContent className="py-12 sm:py-16 text-center">
              <BarChart3 className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-600 mb-2">Aucun rapport généré</h3>
              <p className="text-sm text-gray-500 mb-6 px-4">
                Sélectionnez une période puis cliquez sur "Générer"
              </p>
              <Button 
                onClick={fetchReport}
                className="bg-slate-800 dark:bg-slate-700"
              >
                <FileText className="w-4 h-4 mr-2" />
                Générer mon rapport
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ===== EMAIL RECAP CONFIG ===== */}
        {isAdmin && (
          <Card className="border-0 shadow-md mt-6" data-testid="report-email-config">
            <CardHeader>
              <CardTitle className="text-xl flex items-center space-x-2">
                <Mail className="w-5 h-5 text-slate-700 dark:text-slate-300" />
                <span>Récap par email</span>
              </CardTitle>
              <CardDescription>Recevez automatiquement un récap financier par email</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Status */}
              {emailStatus && emailStatus.status === 'active' && (
                <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="font-medium text-green-700">Scheduler actif</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-green-600">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Heure serveur: </span>
                      <span className="font-medium">{emailStatus.current_server_time}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Prochaine exécution: </span>
                      <span className="font-medium">{emailStatus.next_run}</span>
                    </div>
                    {emailStatus.last_sent && (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 dark:text-gray-400">Dernier envoi: </span>
                        <span className="font-medium">{new Date(emailStatus.last_sent).toLocaleString('fr-FR')}</span>
                        <button
                          type="button"
                          onClick={handleResetLastSent}
                          className="text-xs text-amber-600 hover:text-amber-700 underline ml-2"
                        >
                          (Réinitialiser)
                        </button>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Destinataires: </span>
                      <span className="font-medium">{emailStatus.recipients_count} utilisateur(s)</span>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleEmailSubmit} className="space-y-6">
                {/* Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900/30 rounded-lg border border-gray-200 dark:border-gray-800 dark:border-gray-800">
                  <div className="space-y-0.5">
                    <Label className="text-base font-medium">Activer l'envoi automatique</Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Envoyer un récap financier par email</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEmailConfig({ ...emailConfig, enabled: !emailConfig.enabled })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      emailConfig.enabled ? 'bg-slate-50 dark:bg-slate-900/300' : 'bg-gray-300'
                    }`}
                    data-testid="report-email-toggle"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      emailConfig.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {emailConfig.enabled && (
                  <>
                    {/* Frequency + Time */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Fréquence d'envoi</Label>
                        <Select
                          value={emailConfig.frequency}
                          onValueChange={(value) => setEmailConfig({ ...emailConfig, frequency: value })}
                        >
                          <SelectTrigger data-testid="report-email-frequency">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Tous les jours</SelectItem>
                            <SelectItem value="weekly">Toutes les semaines</SelectItem>
                            <SelectItem value="monthly">Tous les mois</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Heure d'envoi</Label>
                        <Input
                          type="time"
                          value={emailConfig.time_of_day}
                          onChange={(e) => setEmailConfig({ ...emailConfig, time_of_day: e.target.value })}
                          data-testid="report-email-time"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Période du rapport</Label>
                        <Select
                          value={emailConfig.period}
                          onValueChange={(value) => setEmailConfig({ ...emailConfig, period: value })}
                        >
                          <SelectTrigger data-testid="report-email-period">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="this_month">Ce mois</SelectItem>
                            <SelectItem value="last_month">Mois dernier</SelectItem>
                            <SelectItem value="this_quarter">Ce trimestre</SelectItem>
                            <SelectItem value="this_year">Cette année</SelectItem>
                            <SelectItem value="last_year">Année dernière</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Weekly day */}
                    {emailConfig.frequency === 'weekly' && (
                      <div className="space-y-2">
                        <Label>Jour de la semaine</Label>
                        <Select
                          value={emailConfig.custom_day?.toString()}
                          onValueChange={(value) => setEmailConfig({ ...emailConfig, custom_day: parseInt(value) })}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Lundi</SelectItem>
                            <SelectItem value="2">Mardi</SelectItem>
                            <SelectItem value="3">Mercredi</SelectItem>
                            <SelectItem value="4">Jeudi</SelectItem>
                            <SelectItem value="5">Vendredi</SelectItem>
                            <SelectItem value="6">Samedi</SelectItem>
                            <SelectItem value="7">Dimanche</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Monthly day */}
                    {emailConfig.frequency === 'monthly' && (
                      <div className="space-y-2">
                        <Label>Jour du mois</Label>
                        <Select
                          value={emailConfig.custom_day?.toString()}
                          onValueChange={(value) => setEmailConfig({ ...emailConfig, custom_day: parseInt(value) })}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[...Array(28)].map((_, i) => (
                              <SelectItem key={i + 1} value={(i + 1).toString()}>{i + 1}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Template Selector */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Template Email
                      </Label>
                      <Select
                        value={emailConfig.template_id || "default"}
                        onValueChange={(value) => setEmailConfig({ ...emailConfig, template_id: value === "default" ? null : value })}
                      >
                        <SelectTrigger data-testid="report-template-select">
                          <SelectValue placeholder="Template par défaut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">📧 Template par défaut</SelectItem>
                          {emailTemplates.map(t => (
                            <SelectItem key={t.id} value={t.id}>
                              🎨 {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Recipients */}
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Destinataires
                      </Label>
                      <div className="p-4 bg-gray-50 dark:bg-gray-950 rounded-lg border max-h-60 overflow-y-auto space-y-2">
                        {orgUsers.length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400">Aucun utilisateur</p>
                        ) : (
                          orgUsers.map((u) => (
                            <label key={u.id} className="flex items-center gap-3 p-2 hover:bg-white dark:bg-gray-950 rounded cursor-pointer">
                              <Checkbox
                                checked={emailConfig.recipient_user_ids.includes(u.id)}
                                onCheckedChange={() => toggleRecipient(u.id)}
                              />
                              <div className="flex-1">
                                <div className="font-medium text-sm">{u.nom || u.email}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{u.email}</div>
                              </div>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {u.role}
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                      {emailConfig.recipient_user_ids.length === 0 && (
                        <p className="text-xs text-amber-600">Sélectionnez au moins un destinataire</p>
                      )}
                    </div>
                  </>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    type="submit"
                    disabled={savingEmail}
                    className="bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600"
                    data-testid="report-email-save"
                  >
                    {savingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    {savingEmail ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                  
                  {emailConfig.enabled && emailConfig.recipient_user_ids.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleTestEmail}
                      disabled={testingEmail}
                      className="border-gray-200 dark:border-gray-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:bg-slate-900/30"
                      data-testid="report-email-test"
                    >
                      {testingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      {testingEmail ? 'Envoi...' : 'Tester'}
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </StandardPageLayout>
  );
};

export default Rapports;
